import { useCallback, useMemo, useState } from 'react';

/**
 * Snapshot of the credential field values that triggered a duplicate-credentials
 * rejection. Used to derive whether the inline Reuse alert should still be shown
 * (it auto-dismisses as soon as the user edits any of the snapshotted fields).
 */
type CredentialsSnapshot = Record<string, string>;

export interface IUseReuseTestConnectionAlertResult {
  showReuseAlert: boolean;
  setReuseAlertSnapshot: (snapshot: CredentialsSnapshot) => void;
  resetReuseAlert: () => void;
}

/**
 * Manages the state for the "Reuse your test connection account" alert.
 *
 * The alert is set when the backend rejects a create with the duplicate-
 * credentials error and the caller has confirmed the conflict is with this
 * wizard's own draft connection. It auto-dismisses once the user edits any
 * of the credential fields that were submitted at the time of the error,
 * via a render-time comparison against `watchedValues`.
 *
 * @param watchedValues The current form values for the relevant credential
 *   fields (e.g. `step3Form.watch()` or `form.watch('publishedConfig')`).
 */
export function useReuseTestConnectionAlert(
  watchedValues: Record<string, unknown> | undefined,
): IUseReuseTestConnectionAlertResult {
  const [snapshot, setSnapshot] = useState<CredentialsSnapshot | null>(null);

  const showReuseAlert = useMemo(() => {
    if (!snapshot) return false;
    return Object.entries(snapshot).every(
      ([key, value]) => watchedValues?.[key] === value,
    );
  }, [snapshot, watchedValues]);

  const setReuseAlertSnapshot = useCallback(
    (next: CredentialsSnapshot) => setSnapshot(next),
    [],
  );

  const resetReuseAlert = useCallback(() => setSnapshot(null), []);

  return { showReuseAlert, setReuseAlertSnapshot, resetReuseAlert };
}
